import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getSubsistema, useAuth } from "./AuthenticateContext";
import { MenuItem, carrierMenuItems, isCarrierUser, menuItems } from "../utils/menuItems";

export type AcaoPermissao =
  | "ACESSAR"
  | "SALVAR"
  | "REMOVER"
  | "CONHECER"
  | "AUDITAR"
  | "EXPORTAR"
  | string;

/**
 * Descobre a chave de permissão da tela atual a partir da rota, usando os menus
 * como fonte da verdade (é a mesma chave que já controla o ACESSAR no Sidebar e
 * no PrivateRoute). Usa o match mais específico, para que sub-rotas como
 * `/patio/cadastros/motoristas/adicionar` herdem a chave de `motoristas`.
 */
const resolverChaveDaTela = (pathname: string, items: MenuItem[]): string | undefined => {
  let melhor: { path: string; permission: string } | undefined;

  const considerar = (path?: string, permission?: string) => {
    if (!path || !permission) return;
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      if (!melhor || path.length > melhor.path.length) {
        melhor = { path, permission };
      }
    }
  };

  items.forEach((item) => {
    considerar(item.path, item.permission);
    item.submenu?.forEach((sub) => considerar(sub.path, sub.permission));
  });

  return melhor?.permission;
};

/**
 * Permissões da tela atual por ação (ACESSAR, SALVAR, REMOVER, ...).
 *
 * - ADMINISTRADOR libera tudo (mesmo critério do Sidebar/PrivateRoute).
 * - Só valem permissões do subsistema do login (pátio x transportadora
 *   compartilham as mesmas chaves de menu).
 * - Telas que não estão em nenhum menu não têm chave: nesse caso nada é
 *   bloqueado, para não travar tela sem permissão cadastrada. Passe
 *   `chaveManual` para forçar a chave nesses casos.
 */
export const usePermissaoTela = (chaveManual?: string) => {
  const { user, isLoadingPermissions } = useAuth();
  const location = useLocation();

  return useMemo(() => {
    const ehAdministrador = user?.grupo === "ADMINISTRADOR";
    const ehTransportadora = isCarrierUser(user);
    const subsistemaAtual = getSubsistema(user);
    const items = ehTransportadora ? carrierMenuItems : menuItems;
    const chave = chaveManual ?? resolverChaveDaTela(location.pathname, items);

    let permissions: any[] = [];
    try {
      permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      permissions = [];
    }

    const can = (acao: AcaoPermissao): boolean => {
      if (ehAdministrador) return true;
      if (!chave) return true;

      return permissions.some(
        (p: any) =>
          (p.submodule === chave || p.module === chave) &&
          p.action === acao &&
          (!p.subsistema || p.subsistema === subsistemaAtual)
      );
    };

    return {
      chave,
      can,
      podeAcessar: can("ACESSAR"),
      podeSalvar: can("SALVAR"),
      podeRemover: can("REMOVER"),
      podeConhecer: can("CONHECER"),
      podeAuditar: can("AUDITAR"),
      podeExportar: can("EXPORTAR"),
    };
    // isLoadingPermissions entra nas dependências para recalcular quando o
    // /listar/gruposPermissoes termina e grava no localStorage.
  }, [user, isLoadingPermissions, location.pathname, chaveManual]);
};

export default usePermissaoTela;
